import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { toast } from "sonner";

const CartContext = createContext(null);
const UIContext = createContext(null);

const EMPTY_CART = {
  id: null,
  items: [],
  subtotal: 0,
  shipping_cost: 0,
  total: 0,
  min_order_reached: false,
  min_subtotal: 50,
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState(EMPTY_CART);
  const [loading, setLoading] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch (e) {
      // silently ignore; keep empty cart
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addItem = async (product, qty = 1) => {
    setLoading(true);
    try {
      const { data } = await api.post("/cart/items", {
        product_id: product.id,
        quantity: qty,
      });
      setCart(data);
      toast.success(`Añadido al carro`, {
        description: product.name,
      });
    } catch (e) {
      toast.error("No se pudo añadir al carro");
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (productId, quantity) => {
    setLoading(true);
    try {
      const { data } = await api.patch(`/cart/items/${productId}`, { quantity });
      setCart(data);
    } catch (e) {
      toast.error("No se pudo actualizar el carro");
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (productId) => {
    setLoading(true);
    try {
      const { data } = await api.delete(`/cart/items/${productId}`);
      setCart(data);
    } catch (e) {
      toast.error("No se pudo eliminar el producto");
    } finally {
      setLoading(false);
    }
  };

  const clear = async () => {
    setLoading(true);
    try {
      const { data } = await api.delete("/cart");
      setCart(data);
    } finally {
      setLoading(false);
    }
  };

  // Phase 40 — `setOrigin` removed. The `OriginSelector` widget was the only
  // caller; envio shipping is now weight-based (3 €/kg, Phase 34) so a
  // per-origin picker no longer applies. Backend endpoint PATCH /cart/origin
  // is kept for legacy compatibility but is no longer exercised from the UI.

  const itemCount = (cart.items || []).reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ cart, itemCount, loading, addItem, updateItem, removeItem, clear, refresh }}
    >
      <UIContext.Provider
        value={{ cartOpen, setCartOpen, categoryDrawerOpen, setCategoryDrawerOpen }}
      >
        {children}
      </UIContext.Provider>
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
export const useUI = () => useContext(UIContext);
